
import { ProviderKeys, getProviderKey } from '../utils/providerKeys';

const POLLING_INTERVAL = 2000; // 2 seconds

export interface LTXSubmitPayload {
    endpointId: string;
    input: Record<string, any>;
}

export interface LTXResponse {
    request_id: string;
    response_url?: string;
    status_url?: string;
    cancel_url?: string;
    error?: string;
    [key: string]: any;
}

export interface LTXStatus {
    status: 'IN_QUEUE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    logs?: Array<{ message: string; timestamp: string }>;
    metrics?: Record<string, any>;
    [key: string]: any;
}

export interface LTXResult {
    video: { url: string; content_type: string; file_name: string; file_size: number };
    [key: string]: any;
}

/**
 * Service to interact with Vercel LTX proxy functions.
 * Maintains client-side polling logic.
 */
export const LTXService = {

    /**
     * Submit a generation request to LTX queue via Vercel proxy.
     */
    async submit(payload: LTXSubmitPayload): Promise<LTXResponse> {
        // [FAL DEBUG]
        // This method is often called by canary, but also by geminiService for real jobs.
        // We need a way to link the ID if it was already started.
        // For now, if no active context, we might miss the start? 
        // Better: Let the caller (GeminiService) handle the startJob, OR we handle it here if not exists.
        // Actually, let's just log raw requests here as low-level debug.
        // But for the inspector we want "Job ID". 

        // Strategy: Modify submit to take an optional traceId, or just log generally.
        // To respect the "Strict Proof" we want to see EXACTLY what is sent.

        // Since we can't easily change the signature everywhere safely without checking all callers,
        // we will assume GeminiService will handle High Level logging, 
        // BUT for the Canary buttons we need this to work self-contained.

        // If this is a canary call, we want to log it to the inspector.
        // Let's rely on the fact that for real jobs, we will instrument geminiService too.
        // But for "Proof", seeing the raw network call here is valuable.

        const key = getProviderKey(ProviderKeys.FAL_LTX);
        if (!key) throw new Error('Fal.ai API Key not found. Please add it in Settings.');

        const res = await fetch('/api/ltx/submit', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Key ${key}`,
            },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(err.error || `LTX Submit failed: ${res.status}`);
        }

        return res.json();
    },

    /**
     * Check status of a request.
     */
    async checkStatus(endpointId: string, requestId: string, logs = false): Promise<LTXStatus> {
        const key = getProviderKey(ProviderKeys.FAL_LTX);
        if (!key) throw new Error('Fal.ai API Key missing.');

        const url = `/api/ltx/status?endpointId=${encodeURIComponent(endpointId)}&requestId=${encodeURIComponent(requestId)}&logs=${logs}`;

        const res = await fetch(url, {
            method: 'GET',
            headers: { 'Authorization': `Key ${key}` },
        });

        if (!res.ok) {
            throw new Error(`LTX Status check failed: ${res.status}`);
        }

        return res.json();
    },

    /**
     * Get final result.
     */
    async getResult(endpointId: string, requestId: string): Promise<LTXResult> {
        const key = getProviderKey(ProviderKeys.FAL_LTX);
        if (!key) throw new Error('Fal.ai API Key missing.');

        const url = `/api/ltx/result?endpointId=${encodeURIComponent(endpointId)}&requestId=${encodeURIComponent(requestId)}`;

        const res = await fetch(url, {
            method: 'GET',
            headers: { 'Authorization': `Key ${key}` },
        });

        if (!res.ok) {
            throw new Error(`LTX Result fetch failed: ${res.status}`);
        }

        return res.json();
    },

    /**
     * Cancel a request.
     */
    async cancel(endpointId: string, requestId: string): Promise<void> {
        const key = getProviderKey(ProviderKeys.FAL_LTX);
        if (!key) return; // Can't cancel without key

        await fetch('/api/ltx/cancel', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Key ${key}`
            },
            body: JSON.stringify({ endpointId, requestId }),
        }).catch(console.warn);
    },

    /**
     * Polling helper.
     * Resolves with result when complete, rejects on failure/timeout.
     */
    async pollForResult(endpointId: string, requestId: string, onStatus?: (status: LTXStatus) => void): Promise<LTXResult> {
        return new Promise((resolve, reject) => {
            const poll = async () => {
                try {
                    const statusData = await this.checkStatus(endpointId, requestId, true);

                    if (onStatus) onStatus(statusData);

                    if (statusData.status === 'COMPLETED') {
                        // Fetch result
                        const result = await this.getResult(endpointId, requestId);
                        resolve(result);
                    } else if (statusData.status === 'FAILED' || statusData.status === 'CANCELLED') {
                        reject(new Error(`LTX Generation ${statusData.status}: ${JSON.stringify(statusData.error)}`));
                    } else {
                        // Still running (IN_QUEUE or IN_PROGRESS)
                        setTimeout(poll, POLLING_INTERVAL);
                    }
                } catch (err) {
                    reject(err);
                }
            };

            poll();
        });
    }
};
